import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import {
  Check, Sparkles, Loader2, X, AlertCircle, CheckCircle2, Star,
  Package, ArrowRight, ShieldCheck, Zap, Calculator, Crown,
  Radar, Wallet, Layers, LogIn, Plus, Minus, FileText, ScrollText,
  RotateCcw, Lock, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { checkoutServices, subscribeToPlan, type PaymentMethod } from '../api/billing';
import { useEpayPayment } from '../hooks/useEpayPayment';
import PaymentMethodToggle from '../components/PaymentMethodToggle';
import EpayQrModal from '../components/EpayQrModal';
import { ProcurementPlanSummary } from '../components/ProcurementPlanEditor';
import type { ProcurementPlanConfig } from '../api/procurementAdmin';

// ── Types (mirror backend PublicPlan) ────────────────────────────────────────
interface PlanServiceCfg {
  included_qty: number;
  discount_pct: number;
}
interface PublicPlan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  currency: string;
  is_featured: boolean;
  pfu: PlanServiceCfg;
  vip: PlanServiceCfg;
  tracker: PlanServiceCfg;
  planning: PlanServiceCfg;
  tier: PlanServiceCfg;
  experience?: PlanServiceCfg;
  retail_value: number;
  procurement?: ProcurementPlanConfig;
}

interface PriceList {
  pfu_calculation: number;
  vip_calculation: number;
  tracker_slot: number;
  planning_calculation: number;
  tier_requirements_calculation: number;
  experience?: number | null;
  currency: string;
  discounts?: Record<string, number>;
}

type SvcKey = 'pfu' | 'vip' | 'tracker' | 'planning' | 'tier' | 'experience';

const SERVICE_ORDER: { key: SvcKey; labelKey: string }[] = [
  { key: 'pfu', labelKey: 'billing.pfuItem' },
  { key: 'vip', labelKey: 'billing.vipItem' },
  { key: 'tracker', labelKey: 'billing.slotItem' },
  { key: 'planning', labelKey: 'billing.planningItem' },
  { key: 'tier', labelKey: 'billing.tierItem' },
  { key: 'experience', labelKey: 'billing.experienceItem' },
];

// À-la-carte services metadata: price key, balance top-up item_type, icon, i18n keys.
const SERVICES: {
  key: SvcKey;
  itemType: string;
  priceKey: keyof PriceList;
  unitKey: string;
  Icon: React.ComponentType<{ className?: string }>;
  nameKey: string;
  descKey: string;
}[] = [
  { key: 'pfu', itemType: 'pfu_calculation', priceKey: 'pfu_calculation', unitKey: 'plans.unitCalc', Icon: Calculator, nameKey: 'plans.svc.pfuName', descKey: 'plans.svc.pfuDesc' },
  { key: 'vip', itemType: 'vip_calculation', priceKey: 'vip_calculation', unitKey: 'plans.unitCalc', Icon: Crown, nameKey: 'plans.svc.vipName', descKey: 'plans.svc.vipDesc' },
  { key: 'tracker', itemType: 'tracker_slot', priceKey: 'tracker_slot', unitKey: 'plans.unitSlot', Icon: Radar, nameKey: 'plans.svc.trackerName', descKey: 'plans.svc.trackerDesc' },
  { key: 'planning', itemType: 'planning_calculation', priceKey: 'planning_calculation', unitKey: 'plans.unitCalc', Icon: Wallet, nameKey: 'plans.svc.planningName', descKey: 'plans.svc.planningDesc' },
  { key: 'tier', itemType: 'tier_requirements_calculation', priceKey: 'tier_requirements_calculation', unitKey: 'plans.unitCalc', Icon: Layers, nameKey: 'plans.svc.tierName', descKey: 'plans.svc.tierDesc' },
  { key: 'experience', itemType: 'experience', priceKey: 'experience', unitKey: 'plans.unitCalc', Icon: FileText, nameKey: 'plans.svc.experienceName', descKey: 'plans.svc.experienceDesc' },
];

const LEGAL_DOCS: { to: string; labelKey: string; fallback: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { to: '/offer', labelKey: 'plans.legalOffer', fallback: 'Публичная оферта', Icon: ScrollText },
  { to: '/privacy', labelKey: 'plans.legalPrivacy', fallback: 'Политика конфиденциальности', Icon: FileText },
  { to: '/refund', labelKey: 'plans.legalRefund', fallback: 'Возврат и отмена', Icon: RotateCcw },
  { to: '/security', labelKey: 'plans.legalSecurity', fallback: 'Безопасность платежей', Icon: ShieldCheck },
];

function fmtNum(n: number): string {
  return new Intl.NumberFormat('ru-RU').format(n);
}

type Qty = Record<SvcKey, number>;
const EMPTY_QTY: Qty = { pfu: 0, vip: 0, tracker: 0, planning: 0, tier: 0, experience: 0 };

export default function PlansPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, subscription, refreshUser } = useAuth();
  const isGuest = !user;

  // Resolve a translation key, falling back to a provided default if missing.
  const tf = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [prices, setPrices] = useState<PriceList | null>(null);

  const [confirmPlan, setConfirmPlan] = useState<PublicPlan | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saveCard, setSaveCard] = useState(true);

  const [qty, setQty] = useState<Qty>(EMPTY_QTY);
  const [buying, setBuying] = useState(false);
  const [checkoutMethod, setCheckoutMethod] = useState<PaymentMethod>('card');

  const [showTable, setShowTable] = useState(false);

  const [toast, setToast] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const { qrState, qrPolling, handlePaymentInit, closeQr } = useEpayPayment(
    async () => { await refreshUser(); setToast({ type: 'ok', text: t('plans.buySuccess') }); },
    () => setToast({ type: 'err', text: t('plans.qrFailed') }),
  );

  const load = () => {
    setLoading(true);
    setError('');
    api.get<PublicPlan[]>('/subscriptions/plans')
      .then((r) => setPlans(r.data))
      .catch(() => setError(t('plans.loadError')))
      .finally(() => setLoading(false));
  };

  const loadPrices = () => {
    // Authenticated users get personal (subscription) discounts; guests see retail.
    const url = user ? '/billing/prices' : '/billing/prices/public';
    api.get<PriceList>(url)
      .then((r) => setPrices(r.data))
      .catch(() => setPrices(null));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadPrices(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const currentPlanId = subscription?.active ? subscription.plan_id : null;
  const hasActive = !!subscription?.active && subscription.status === 'active';

  const goLogin = () => navigate('/login', { state: { from: '/plans' } });

  const handlePlanCta = (plan: PublicPlan, isCurrent: boolean) => {
    if (isCurrent) return;
    if (isGuest) { goLogin(); return; }
    setSaveCard(true);
    setConfirmPlan(plan);
  };

  const doSubscribe = async () => {
    if (!confirmPlan) return;
    setSubmitting(true);
    try {
      const init = await subscribeToPlan(confirmPlan.id, saveCard);
      setConfirmPlan(null);
      await handlePaymentInit(init);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setToast({ type: 'err', text: typeof detail === 'string' ? detail : t('plans.subscribeError') });
      setConfirmPlan(null);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Service cart helpers ────────────────────────────────────────────────────
  const discOf = (priceKey: string) => prices?.discounts?.[priceKey] ?? 0;
  const netPrice = (price: number, pct: number) => Math.max(0, Math.round(price * (1 - pct / 100)));
  const setQtyFor = (k: SvcKey, v: number) => setQty((q) => ({ ...q, [k]: Math.min(999, Math.max(0, v)) }));

  const cart = useMemo(() => {
    if (!prices) return { totalNet: 0, totalFull: 0, count: 0 };
    let totalNet = 0, totalFull = 0, count = 0;
    for (const s of SERVICES) {
      const n = qty[s.key];
      if (n <= 0) continue;
      const base = prices[s.priceKey] as number;
      if (base == null) continue;
      totalNet += n * netPrice(base, prices.discounts?.[s.priceKey] ?? 0);
      totalFull += n * base;
      count += n;
    }
    return { totalNet, totalFull, count };
  }, [qty, prices]);

  const handleCheckout = async () => {
    if (isGuest) { goLogin(); return; }
    const items = SERVICES.filter((s) => qty[s.key] > 0 && prices?.[s.priceKey] != null).map((s) => ({ item_type: s.itemType, quantity: qty[s.key] }));
    if (items.length === 0) return;
    setBuying(true);
    try {
      const init = await checkoutServices(items, checkoutMethod);
      setQty(EMPTY_QTY);
      await handlePaymentInit(init);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setToast({ type: 'err', text: typeof detail === 'string' ? detail : t('plans.buyError') });
    } finally {
      setBuying(false);
    }
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const gridCols = useMemo(() => {
    const n = plans.length;
    if (n <= 1) return 'md:grid-cols-1 max-w-md';
    if (n === 2) return 'md:grid-cols-2 max-w-3xl';
    return 'md:grid-cols-2 xl:grid-cols-3 max-w-6xl';
  }, [plans.length]);

  const anyDiscount = SERVICES.some((s) => discOf(s.priceKey) > 0);

  return (
    <div className="min-h-full bg-slate-50 font-sans">
      <div className="p-4 sm:p-8 lg:p-10 max-w-6xl mx-auto w-full">

        {/* Hero */}
        <div className="text-center mb-8 sm:mb-10">
          <span className="inline-flex items-center gap-2 bg-goszakup-50 border border-goszakup-100 rounded-full px-4 py-1.5 text-xs font-bold text-goszakup-700 mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            {t('plans.heroTag')}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            {t('plans.heroTitle')}
          </h1>
          <p className="text-gray-500 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            {t('plans.heroSubtitle')}
          </p>
        </div>

        {/* Anchor nav */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10">
          {[
            { id: 'subscriptions', label: t('plans.navSubscriptions') },
            { id: 'services', label: t('plans.navServices') },
            { id: 'pricing', label: t('plans.navTable') },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className="px-4 py-2 rounded-full bg-white border border-gray-200 text-xs sm:text-sm font-bold text-gray-600 hover:text-goszakup-700 hover:border-goszakup-200 transition-colors"
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Guest banner */}
        {isGuest && (
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center gap-3 bg-white border border-goszakup-100 rounded-2xl px-5 py-4 shadow-sm max-w-3xl mx-auto">
            <div className="w-10 h-10 rounded-xl bg-goszakup-50 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-goszakup-600" />
            </div>
            <p className="text-sm text-gray-600 leading-snug flex-1">{t('plans.guestNote')}</p>
            <button
              onClick={goLogin}
              className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-goszakup-600 hover:bg-goszakup-700 px-4 py-2.5 rounded-xl transition-colors"
            >
              <LogIn className="w-3.5 h-3.5" /> {t('plans.loginToBuy')}
            </button>
          </div>
        )}

        {/* Current subscription banner */}
        {hasActive && subscription?.plan_name && (
          <div className="mb-8 flex items-center gap-3 bg-white border border-emerald-100 rounded-2xl px-5 py-4 shadow-sm max-w-3xl mx-auto">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900">
                {t('plans.currentBannerTitle')} <span className="text-emerald-700">{subscription.plan_name}</span>
              </p>
              <p className="text-xs text-gray-400">{t('plans.currentBannerHint')}</p>
            </div>
            <button
              onClick={() => navigate('/profile#subscription')}
              className="ml-auto flex-shrink-0 text-xs font-bold text-goszakup-600 hover:text-goszakup-700 flex items-center gap-1"
            >
              {t('plans.manage')} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Toast */}
        {toast && ReactDOM.createPortal(
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[220] max-w-sm w-[calc(100%-2rem)]">
            <div className={`flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-semibold ${toast.type === 'ok' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
              {toast.type === 'ok' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
              {toast.text}
              <button onClick={() => setToast(null)} className="ml-auto"><X className="w-4 h-4" /></button>
            </div>
          </div>,
          document.body,
        )}

        {/* ═══ SUBSCRIPTIONS ═══ */}
        <section id="subscriptions" className="scroll-mt-6 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">{t('plans.navSubscriptions')}</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-9 h-9 border-4 border-goszakup-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <p className="text-red-500 font-medium">{error}</p>
              <button onClick={load} className="px-4 py-2 rounded-xl bg-goszakup-600 text-white text-sm font-bold">
                {t('plans.retry')}
              </button>
            </div>
          ) : plans.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-gray-400">
              <Package className="w-12 h-12 opacity-30" />
              <p className="text-sm">{t('plans.empty')}</p>
            </div>
          ) : (
            <div className={`grid grid-cols-1 ${gridCols} gap-6 mx-auto`}>
              {plans.map((plan) => {
                const isCurrent = plan.id === currentPlanId;
                const savings = plan.retail_value > plan.price_monthly && plan.price_monthly > 0
                  ? Math.round((1 - plan.price_monthly / plan.retail_value) * 100)
                  : 0;
                const featured = plan.is_featured;
                return (
                  <div
                    key={plan.id}
                    className={`relative flex flex-col rounded-3xl p-6 sm:p-7 transition-all duration-300 ${
                      featured
                        ? 'bg-gradient-to-br from-goszakup-700 to-blue-700 text-white shadow-2xl shadow-goszakup-600/20 xl:-translate-y-2'
                        : 'bg-white border border-gray-100 shadow-sm hover:shadow-lg'
                    }`}
                  >
                    {featured && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-amber-400 text-amber-950 text-[11px] font-extrabold px-3 py-1 rounded-full shadow">
                        <Star className="w-3 h-3 fill-amber-950" /> {t('plans.popular')}
                      </span>
                    )}

                    <div className="mb-5">
                      <h3 className={`text-xl font-extrabold mb-1 ${featured ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                      {plan.description && (
                        <p className={`text-sm leading-snug ${featured ? 'text-white/70' : 'text-gray-400'}`}>{plan.description}</p>
                      )}
                    </div>

                    <div className="mb-1 flex items-baseline gap-1.5">
                      <span className={`text-4xl font-extrabold tracking-tight ${featured ? 'text-white' : 'text-gray-900'}`}>
                        {fmtNum(plan.price_monthly)}
                      </span>
                      <span className={`text-lg font-bold ${featured ? 'text-white/70' : 'text-gray-400'}`}>₸</span>
                      <span className={`text-sm ${featured ? 'text-white/60' : 'text-gray-400'}`}>/ {t('plans.perMonth')}</span>
                    </div>

                    {savings > 0 ? (
                      <p className={`text-xs font-semibold mb-5 ${featured ? 'text-amber-300' : 'text-emerald-600'}`}>
                        {t('plans.valueLabel')} ~{fmtNum(plan.retail_value)} ₸ · {t('plans.savings')} {savings}%
                      </p>
                    ) : (
                      <div className="mb-5 h-4" />
                    )}

                    <button
                      onClick={() => handlePlanCta(plan, isCurrent)}
                      disabled={isCurrent}
                      className={`w-full py-3 rounded-2xl text-sm font-extrabold transition-all flex items-center justify-center gap-2 mb-6 ${
                        isCurrent
                          ? featured ? 'bg-white/20 text-white/80 cursor-default' : 'bg-gray-100 text-gray-400 cursor-default'
                          : featured
                            ? 'bg-white text-goszakup-700 hover:bg-slate-50 shadow-lg'
                            : 'bg-goszakup-600 text-white hover:bg-goszakup-700 shadow-sm'
                      }`}
                    >
                      {isCurrent ? (
                        <><CheckCircle2 className="w-4 h-4" /> {t('plans.currentPlan')}</>
                      ) : isGuest ? (
                        <><LogIn className="w-4 h-4" /> {t('plans.loginToSubscribe')}</>
                      ) : hasActive ? (
                        <><Zap className="w-4 h-4" /> {t('plans.switchPlan')}</>
                      ) : (
                        <>{t('plans.subscribe')} <ArrowRight className="w-4 h-4" /></>
                      )}
                    </button>

                    <div className={`mb-4 ${featured ? 'text-white' : 'text-gray-700'}`}><ProcurementPlanSummary value={plan.procurement} /></div>
                    <ul className="space-y-2.5 flex-1">
                      {SERVICE_ORDER.map(({ key, labelKey }) => {
                        const cfg = plan[key] ?? { included_qty: 0, discount_pct: 0 };
                        if (cfg.included_qty > 0) {
                          return (
                            <li key={key} className="flex items-center gap-2.5 text-sm">
                              <Check className={`w-4 h-4 flex-shrink-0 ${featured ? 'text-emerald-300' : 'text-emerald-500'}`} />
                              <span className={featured ? 'text-white/90' : 'text-gray-700'}>{t(labelKey)}</span>
                              <span className={`ml-auto font-bold ${featured ? 'text-white' : 'text-gray-900'}`}>×{cfg.included_qty}</span>
                            </li>
                          );
                        }
                        if (cfg.discount_pct > 0) {
                          return (
                            <li key={key} className="flex items-center gap-2.5 text-sm">
                              <span className={`w-4 h-4 rounded-full border flex-shrink-0 ${featured ? 'border-white/30' : 'border-gray-300'}`} />
                              <span className={featured ? 'text-white/60' : 'text-gray-400'}>{t(labelKey)}</span>
                              <span className={`ml-auto font-bold ${featured ? 'text-amber-300' : 'text-goszakup-600'}`}>−{cfg.discount_pct}%</span>
                            </li>
                          );
                        }
                        return (
                          <li key={key} className="flex items-center gap-2.5 text-sm opacity-40">
                            <X className={`w-4 h-4 flex-shrink-0 ${featured ? 'text-white/40' : 'text-gray-300'}`} />
                            <span className={featured ? 'text-white/50' : 'text-gray-400'}>{t(labelKey)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ═══ À-LA-CARTE SERVICES ═══ */}
        <section id="services" className="scroll-mt-6 mb-16">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-xs font-bold text-gray-600 mb-4">
              <Package className="w-3.5 h-3.5" /> {t('plans.servicesTag')}
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight mb-3">{t('plans.servicesTitle')}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">{t('plans.servicesSubtitle')}</p>
          </div>

          {anyDiscount && !isGuest && subscription?.active && (
            <div className="max-w-3xl mx-auto mb-6 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-100 text-xs font-semibold text-amber-700">
              <Sparkles className="w-4 h-4 flex-shrink-0" />
              {t('billing.discountBanner').replace('{plan}', subscription.plan_name ?? '')}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map(({ key, priceKey, unitKey, Icon, nameKey, descKey }) => {
              const base = prices ? (prices[priceKey] as number) : null;
              const pct = discOf(priceKey);
              const net = base != null ? netPrice(base, pct) : null;
              const n = qty[key];
              return (
                <div key={key} className="flex flex-col bg-white rounded-3xl border border-gray-100 shadow-sm p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-11 h-11 rounded-2xl bg-goszakup-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-goszakup-600" />
                    </div>
                    <div className="min-w-0 flex items-center">
                      <h3 className="text-base font-extrabold text-gray-900 leading-snug">{t(nameKey)}</h3>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500 leading-relaxed flex-1 mb-5">{t(descKey)}</p>

                  <div className="mb-4">
                    {base == null ? (
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        {!prices && <Loader2 className="w-4 h-4 animate-spin" />} {t(prices ? 'billing.priceNotConfigured' : 'billing.priceLoading')}
                      </div>
                    ) : pct > 0 ? (
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm text-gray-400 line-through">{fmtNum(base)} ₸</span>
                        <span className="text-2xl font-extrabold text-goszakup-700">{fmtNum(net as number)}</span>
                        <span className="text-base font-bold text-gray-400">₸</span>
                        <span className="inline-block px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-extrabold">−{pct}%</span>
                        <span className="text-xs text-gray-400">/ {t(unitKey)}</span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl font-extrabold text-gray-900">{fmtNum(base)}</span>
                        <span className="text-base font-bold text-gray-400">₸</span>
                        <span className="text-xs text-gray-400">/ {t(unitKey)}</span>
                      </div>
                    )}
                  </div>

                  {base == null ? (
                    key === 'experience' ? <Link to="/experience" className="text-sm font-semibold text-goszakup-700 hover:underline">{t('billing.experienceDetails')}</Link> : null
                  ) : isGuest ? (
                    <button
                      onClick={goLogin}
                      className="w-full py-2.5 rounded-2xl text-sm font-extrabold text-goszakup-700 bg-goszakup-50 hover:bg-goszakup-100 transition-colors flex items-center justify-center gap-2"
                    >
                      <LogIn className="w-4 h-4" /> {t('plans.loginToBuy')}
                    </button>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <button
                          type="button"
                          onClick={() => setQtyFor(key, n - 1)}
                          className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold flex items-center justify-center transition-colors"
                          aria-label="minus"
                        ><Minus className="w-4 h-4" /></button>
                        <input
                          type="number"
                          min={0}
                          max={999}
                          value={n}
                          onChange={(e) => {
                            const raw = parseInt(e.target.value, 10);
                            setQtyFor(key, isNaN(raw) ? 0 : raw);
                          }}
                          className="w-14 h-9 text-center font-extrabold text-gray-900 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-goszakup-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button
                          type="button"
                          onClick={() => setQtyFor(key, n + 1)}
                          className="w-9 h-9 rounded-xl bg-goszakup-100 hover:bg-goszakup-200 text-goszakup-700 font-bold flex items-center justify-center transition-colors"
                          aria-label="plus"
                        ><Plus className="w-4 h-4" /></button>
                      </div>
                      {n > 0 && base != null && (
                        <span className="text-sm font-extrabold text-gray-900">{fmtNum(n * (net as number))} ₸</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Cart / checkout bar */}
          {!isGuest && cart.count > 0 && (
            <div className="mt-6 max-w-3xl mx-auto bg-white rounded-3xl border border-gray-100 shadow-sm p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('plans.cartTitle')}</p>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    {cart.totalNet !== cart.totalFull && (
                      <span className="text-sm text-gray-400 line-through">{fmtNum(cart.totalFull)} ₸</span>
                    )}
                    <span className="text-2xl font-extrabold text-gray-900">{fmtNum(cart.totalNet)} ₸</span>
                    {cart.totalNet !== cart.totalFull && (
                      <span className="text-xs font-bold text-emerald-600">
                        {t('billing.youSave').replace('{amount}', fmtNum(cart.totalFull - cart.totalNet))}
                      </span>
                    )}
                  </div>
                </div>
                <PaymentMethodToggle value={checkoutMethod} onChange={setCheckoutMethod} t={t} />
                <button
                  onClick={handleCheckout}
                  disabled={buying}
                  className="sm:w-auto w-full py-3.5 px-8 rounded-2xl text-sm font-extrabold text-white bg-goszakup-600 hover:bg-goszakup-700 transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {buying ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('plans.buyProcessing')}</> : <>{t('plans.buyNow')} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ═══ FULL PRICE TABLE (collapsible) ═══ */}
        <section id="pricing" className="scroll-mt-6 mb-16">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-xs font-bold text-gray-600 mb-4">
              <FileText className="w-3.5 h-3.5" /> {t('plans.tableTag')}
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight mb-3">{t('plans.tableTitle')}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">{t('plans.tableSubtitle')}</p>
          </div>

          <div className="flex justify-center">
            <button
              onClick={() => setShowTable((v) => !v)}
              aria-expanded={showTable}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-white border border-gray-200 text-sm font-extrabold text-gray-700 hover:text-goszakup-700 hover:border-goszakup-200 shadow-sm transition-all"
            >
              {showTable ? t('plans.hideTable') : t('plans.showTable')}
              {showTable ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {showTable && (
            <div className="mt-6 bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[640px]">
                  <thead>
                    <tr className="bg-slate-50 text-gray-500 text-xs uppercase tracking-wider">
                      <th className="px-5 py-4 font-bold">{t('plans.colService')}</th>
                      <th className="px-5 py-4 font-bold whitespace-nowrap">{t('plans.colUnit')}</th>
                      <th className="px-5 py-4 font-bold text-right whitespace-nowrap">{t('plans.colPrice')}</th>
                      <th className="px-5 py-4 font-bold">{t('plans.colDesc')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {/* Subscriptions group */}
                    {plans.length > 0 && (
                      <tr className="bg-goszakup-50/40">
                        <td colSpan={4} className="px-5 py-2.5 text-xs font-extrabold text-goszakup-700 uppercase tracking-wider">
                          {t('plans.tableSubsGroup')}
                        </td>
                      </tr>
                    )}
                    {plans.map((plan) => (
                      <tr key={plan.id} className="text-sm hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-4 font-bold text-gray-900 align-top">{plan.name}</td>
                        <td className="px-5 py-4 text-gray-500 align-top whitespace-nowrap">{t('plans.unitMonth')}</td>
                        <td className="px-5 py-4 text-right font-extrabold text-gray-900 align-top whitespace-nowrap">{fmtNum(plan.price_monthly)} ₸</td>
                        <td className="px-5 py-4 text-gray-500 align-top leading-relaxed"><p>{plan.description || '—'}</p><ProcurementPlanSummary value={plan.procurement} /></td>
                      </tr>
                    ))}

                    {/* Services group */}
                    <tr className="bg-goszakup-50/40">
                      <td colSpan={4} className="px-5 py-2.5 text-xs font-extrabold text-goszakup-700 uppercase tracking-wider">
                        {t('plans.tableServicesGroup')}
                      </td>
                    </tr>
                    {SERVICES.map(({ key, priceKey, unitKey, nameKey, descKey }) => {
                      const base = prices ? (prices[priceKey] as number) : null;
                      return (
                        <tr key={key} className="text-sm hover:bg-slate-50/60 transition-colors">
                          <td className="px-5 py-4 font-bold text-gray-900 align-top">{t(nameKey)}</td>
                          <td className="px-5 py-4 text-gray-500 align-top whitespace-nowrap">{t(unitKey)}</td>
                          <td className="px-5 py-4 text-right font-extrabold text-gray-900 align-top whitespace-nowrap">
                            {base != null ? `${fmtNum(base)} ₸` : '—'}
                          </td>
                          <td className="px-5 py-4 text-gray-500 align-top leading-relaxed">{t(descKey)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-center p-5 border-t border-gray-100">
                <button
                  onClick={() => setShowTable(false)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-2xl bg-slate-50 border border-gray-200 text-sm font-extrabold text-gray-600 hover:text-goszakup-700 hover:border-goszakup-200 transition-all"
                >
                  {t('plans.hideTable')} <ChevronUp className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ═══ LEGAL BLOCK ═══ */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-5">
            <ShieldCheck className="w-5 h-5 text-goszakup-600" />
            <h3 className="text-base font-extrabold text-gray-900">{t('plans.legalTitle')}</h3>
          </div>
          <div className="space-y-3 text-sm text-gray-500 leading-relaxed">
            <p>{t('plans.legalOperator')}</p>
            <p>{t('plans.legalCurrency')}</p>
            <p className="font-semibold text-gray-600">{t('plans.legalDocs')}</p>
          </div>
          <div className="flex flex-wrap gap-2.5 mt-4">
            {LEGAL_DOCS.map(({ to, labelKey, fallback, Icon }) => (
              <Link
                key={to}
                to={to}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-50 border border-gray-100 text-xs font-bold text-gray-600 hover:text-goszakup-700 hover:border-goszakup-200 transition-colors"
              >
                <Icon className="w-3.5 h-3.5" /> {tf(labelKey, fallback)}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Confirm modal (subscription) */}
      {confirmPlan && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !submitting && setConfirmPlan(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 rounded-full bg-goszakup-50 flex items-center justify-center mx-auto mb-4">
              <Package className="w-6 h-6 text-goszakup-600" />
            </div>
            <h3 className="font-extrabold text-center text-gray-900 mb-2">
              {hasActive ? t('plans.confirmSwitchTitle') : t('plans.confirmTitle')}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-1 leading-relaxed">
              {(hasActive ? t('plans.confirmSwitchBody') : t('plans.confirmBody'))
                .replace('{plan}', confirmPlan.name)
                .replace('{price}', fmtNum(confirmPlan.price_monthly))}
            </p>
            <p className="text-[11px] text-gray-400 text-center mb-4">{t('plans.confirmTestNote')}</p>
            <div className="mb-4"><ProcurementPlanSummary value={confirmPlan.procurement} /></div>
            <label className="mb-5 flex items-start gap-2.5 px-1 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-goszakup-600 focus:ring-goszakup-500"
              />
              <span className="text-xs text-gray-500 leading-relaxed">
                {tf('plans.saveCardLabel', 'Сохранить карту для автопродления подписки')}
                <span className="block text-[10px] text-gray-400 mt-0.5">
                  {tf('plans.saveCardHint', 'Данные карты хранятся только в Halyk Bank. Карту можно удалить в профиле в любой момент.')}
                </span>
              </span>
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmPlan(null)}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-60"
              >
                {t('plans.cancel')}
              </button>
              <button
                onClick={doSubscribe}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-xl bg-goszakup-600 text-white text-sm font-bold hover:bg-goszakup-700 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {submitting ? t('plans.processing') : t('plans.confirmBtn')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {qrState && (
        <EpayQrModal qr={qrState.qr} polling={qrPolling} onClose={closeQr} t={t} />
      )}
    </div>
  );
}
